class AutoplaySlider {
  constructor(element) {
    this.track = element;
    this.slides = [...element.children].filter((el) => el.tagName !== "STYLE");
    this.currentIndex = 0;
    this.autoplayDelay = parseInt(element.dataset.autoplayDelay) || 3000;
    this.autoplayInterval = null;
    this.sliderMode = element.dataset.sliderMode || "slide";
    this.marqueeScope = element.dataset.marqueeScope || "mobile";
    this.container = element.closest(".autoplay-slider");
    this.cloned = false;
    this.clones = [];
    this.isDesktop = window.matchMedia("(min-width: 64rem)").matches;

    this.init();
  }

  init() {
    if (this.sliderMode === "marquee" && this.slides.length > 0) {
      this.applyMarquee();
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.applyMarquee());
      }
      window.addEventListener("resize", () => {
        clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => {
          this.isDesktop = window.matchMedia("(min-width: 64rem)").matches;
          this.applyMarquee();
        }, 150);
      });
      return;
    }

    if (this.isDesktop || this.slides.length <= 1) {
      this.showAll();
      return;
    }

    this.goTo(0);
    this.startAutoplay();
    this.bindArrows();
    this.bindHoverPause();
    this.bindTouchPause();

    window.addEventListener("resize", () => {
      const wasDesktop = this.isDesktop;
      this.isDesktop = window.matchMedia("(min-width: 64rem)").matches;

      if (this.isDesktop !== wasDesktop) {
        if (this.isDesktop) {
          this.stopAutoplay();
          this.showAll();
        } else {
          this.goTo(this.currentIndex);
          this.startAutoplay();
        }
      }
    });
  }

  marqueeActive() {
    if (this.sliderMode !== "marquee") return false;
    if (this.marqueeScope === "both") return true;
    return !this.isDesktop;
  }

  applyMarquee() {
    // Reset to a clean, un-cloned, non-marquee state for an accurate measurement.
    if (this.cloned) {
      this.clones.forEach((clone) => clone.remove());
      this.clones = [];
      this.cloned = false;
    }
    this.track.style.transform = "";
    this.track.classList.remove("is-marquee");
    if (this.container) this.container.classList.remove("is-marquee");

    if (!this.marqueeActive()) return;
    // On desktop, only scroll when the items actually overflow the viewport.
    if (this.isDesktop && !this.contentOverflows()) return;

    this.clones = this.slides.map((slide) => {
      const clone = slide.cloneNode(true);
      clone.setAttribute("aria-hidden", "true");
      this.track.appendChild(clone);
      return clone;
    });
    this.cloned = true;
    this.track.classList.add("is-marquee");
    if (this.container) this.container.classList.add("is-marquee");
  }

  contentOverflows() {
    const viewport = this.track.parentElement;
    if (!viewport) return true;
    // Lay items out at natural width (no clones present), then compare to the viewport.
    this.track.classList.add("is-marquee");
    // The last item's margin-right is loop spacing, not real content — exclude it.
    const last = this.track.lastElementChild;
    const trailing = last ? parseFloat(getComputedStyle(last).marginRight) || 0 : 0;
    const contentWidth = viewport.scrollWidth - trailing;
    this.track.classList.remove("is-marquee");
    return contentWidth > viewport.clientWidth + 1;
  }

  goTo(index) {
    this.currentIndex = index;
    this.track.style.transform = `translateX(${-index * 100}%)`;
  }

  showAll() {
    this.track.style.transform = "translateX(0%)";
  }

  nextSlide() {
    this.goTo((this.currentIndex + 1) % this.slides.length);
  }

  prevSlide() {
    this.goTo((this.currentIndex - 1 + this.slides.length) % this.slides.length);
  }

  startAutoplay() {
    if (this.autoplayInterval) return;
    this.autoplayInterval = setInterval(() => this.nextSlide(), this.autoplayDelay);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }

  bindArrows() {
    const container = this.track.closest(".autoplay-slider");
    if (!container) return;

    const prevBtn = container.querySelector("[data-autoplay-prev]");
    const nextBtn = container.querySelector("[data-autoplay-next]");

    if (prevBtn) {
      prevBtn.addEventListener("click", () => {
        this.prevSlide();
        this.restartAutoplay();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener("click", () => {
        this.nextSlide();
        this.restartAutoplay();
      });
    }
  }

  bindHoverPause() {
    const container = this.track.closest(".autoplay-slider");
    if (!container) return;

    container.addEventListener("mouseenter", () => this.stopAutoplay());
    container.addEventListener("mouseleave", () => {
      if (!this.isDesktop) this.startAutoplay();
    });
  }

  bindTouchPause() {
    this.track.addEventListener("touchstart", () => this.stopAutoplay(), { passive: true });
    this.track.addEventListener(
      "touchend",
      () => {
        if (!this.isDesktop) this.startAutoplay();
      },
      { passive: true },
    );
  }
}

const initAutoplaySlider = (el) => {
  if (!el || el.dataset.autoplaySliderInit === "1") return;
  el.dataset.autoplaySliderInit = "1";
  new AutoplaySlider(el);
};

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll("[data-autoplay-slider]").forEach(initAutoplaySlider);
});

if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener("shopify:section:load", (event) => {
    initAutoplaySlider(event.target.querySelector("[data-autoplay-slider]"));
  });
}
